<template>
    <CModal
        size="xl"
        alignment="center"
        :visible="showModal"
        @close="getVacation"
    >
        <CModalHeader>
            <CModalTitle>{{ $t('base.getVacation') }}</CModalTitle>
        </CModalHeader>

        <CModalBody>
            <CButton
                v-show="defaultState"
                @click.prevent="defaultState = false"
                color="primary"
                size="sm"
                class="float-end"
                >{{ $t('sidebar.getVacation') }}</CButton
            >
            <VacationTable v-show="defaultState" />

            <div class="days-count">
                {{ $t('profile.usedDays') }}: {{getVacationsCount.vacation.used_count.with_pay}} / {{getVacationsCount.vacation.max_count}}
            </div>
            <div v-if="getVacationsCount.vacation.used_count >= getVacationsCount.vacation.max_count" class="days-count-warn">
                {{ $t('profile.vacationWarning') }}
            </div>

            <CForm v-show="!defaultState">
                <div class="mb-3">
                    <CFormLabel>{{
                        $t('getVacation.form.chooseDuration')
                    }}</CFormLabel>
                    <select
                        ref="duration"
                        v-model="v$.duration.$model"
                        @change="checkDuration"
                        class="select"
                    >
                        <option value="">
                            {{ $t('getVacation.form.duration') }}
                        </option>
                        <option value="period">
                            {{ $t('getVacation.form.period') }}
                        </option>
                        <option value="day">
                            {{ $t('getVacation.form.day') }}
                        </option>
                    </select>

                    <small
                        v-if="v$.duration.$error"
                        class="helper-text invalid"
                        >{{ $t('getVacation.form.chooseDuration') }}</small
                    >
                </div>

                <div v-if="durationType === 'day'" class="mb-3">
                    <CFormLabel>{{ $t('calendar.form.date') }}</CFormLabel>
                    <Datepicker
                        ref="date"
                        :min-date="new Date()"
                        v-model="date"
                        :enable-time-picker="false"
                        :disabled-week-days="[6,0]"
                        :highlight-week-days="[6, 0]"
                        teleport-center
                    ></Datepicker>

                    <small
                        v-if="v$.date.$dirty && v$.date.$error"
                        class="helper-text invalid"
                        >{{ $t('calendar.form.dateValidation') }}</small
                    >
                </div>

                <div v-if="durationType === 'period'" class="mb-3">
                    <CFormLabel>{{
                        $t('calendar.form.dateBeginning')
                    }}</CFormLabel>

                    <Datepicker
                        @update:modelValue="setMinDate"
                        v-model="start"
                        :min-date="minDate"
                        :max-date="maxDate"
                        ref="start"
                        :disabled-week-days="[6,0]"
                        :highlight-week-days="[6, 0]"
                        :enable-time-picker="false"
                        teleport-center
                    ></Datepicker>

                    <small
                        v-if="v$.start.$dirty && v$.start.$error"
                        class="helper-text invalid"
                        >{{
                            $t('calendar.form.dateBeginningValidation')
                        }}</small
                    >
                </div>

                <div v-if="durationType === 'period'" class="mb-3">
                    <CFormLabel>{{
                        $t('calendar.form.dateEnding')
                    }}</CFormLabel>

                    <Datepicker
                        @update:modelValue="setMaxDate"
                        v-model="end"
                        :min-date="minDateSecondary"
                        ref="end"
                        :disabled-week-days="[6,0]"
                        :highlight-week-days="[6, 0]"
                        :enable-time-picker="false"
                        teleport-center
                    ></Datepicker>

                    <small
                        v-if="v$.end.$dirty && v$.end.$error"
                        class="helper-text invalid"
                        >{{ $t('calendar.form.dateEndingValidation') }}</small
                    >
                </div>
                <div class="days-count-warn" v-if="showWPwarn">
                    {{ $t('profile.WPwarn') }}
                </div>
                <CFormCheck id="flexCheckDefaultVModel" class="checkbox-pay" :label=" $t('hr.withoutPay')" v-model="withoutPay" />
            </CForm>
        </CModalBody>

        <CModalFooter v-show="!defaultState">
            <CButton color="secondary" size="sm" @click="getVacation">
                {{ $t('general.close') }}
            </CButton>
            <CButton
                @click="handleDateSubmit"
                :disabled="requestStatus"
                color="primary"
                size="sm"
                >{{ $t('general.confirm') }}</CButton
            >
        </CModalFooter>
    </CModal>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import VacationTable from '@/components/vacation/VacationTable.vue'
import { reactive, toRefs, computed } from 'vue'
import { useStore } from 'vuex'
import fireSwal from '@/helpers/fireSwal'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

export default {
    name: 'GetVacationModal',
    components: {
        VacationTable,
        Datepicker,
    },
    emits: ['getVacation', 'closeVacationModal'],
    props: {
        showModal: {
            type: Boolean,
            required: true,
        },
    },
    setup() {
        const store = useStore()

        const state = reactive({
            v$: useVuelidate(),
            defaultState: false,
            minDate: new Date(),
            minDateSecondary: new Date(),
            maxDate: null,
            tableData: computed(() => store.getters['events/getVacations']),
            user: computed(
                () => store.getters['authorization/getUserNickname'],
            ),
            currentUser: computed(
                () => store.getters['authorization/getCurrentUser'],
            ),
        })

        return { ...toRefs(state), store }
    },
    data() {
        return {
            showWPwarn: false,
            withoutPay: false,
            dateErrors: [],
            type: '',
            duration: '',
            start: null,
            end: null,
            date: null,
            durationType: '',
            requestStatus: false,
        }
    },
    validations() {
        return {
            duration: { required },
            type: { required },
            date: { required },
            start: { required },
            end: { required },
        }
    },


    computed: {
      getVacationsCount() {
          return this.$store.getters['hr/getVacationsCount']
      }
    },
    methods: {
        getVacation() {
            this.v$.$reset()
            this.showWPwarn = false
            this.requestStatus = false
            this.date = null
            this.start = null
            this.end = null
            this.durationType = ''
            this.duration = ''
            this.minDate = new Date().toISOString().split('T')[0]
            this.minDateSecondary = new Date()
                .toISOString()
                .split('T')[0]
            this.maxDate = null
            this.$emit('getVacation', false)
        },
        closeVacationModal() {
            this.defaultState = true
        },
        checkDuration() {
            this.durationType = this.$refs.duration.value
        },
        setMinDate(val) {
            this.minDateSecondary = val
        },
        setMaxDate(val) {
            this.maxDate = val
        },
        handleDateSubmit() {
            if ((this.getVacationsCount.vacation.used_count >= this.getVacationsCount.vacation.max_count) && !this.withoutPay) {
                this.showWPwarn = true
                return
            } else {
            this.dateErrors = []
            this.v$.$silentErrors.forEach((item) => {
                this.dateErrors.push(item.$property)
            })

            if (
                this.durationType === '' ||
                (this.v$.$invalid &&
                    (this.dateErrors.indexOf('start') !== -1 ||
                        this.dateErrors.indexOf('end') !== -1) &&
                    this.durationType === 'period') ||
                (this.v$.$invalid &&
                    this.dateErrors.indexOf('date') !== -1 &&
                    this.durationType === 'day')
            ) {
                this.v$.$touch()
                return
            }

            this.requestStatus = true

            this.$store
                .dispatch(`hr/addVacation`, {
                    form: {
                        start:
                            this.durationType === 'period'
                                ? new Date(this.start)
                                : new Date(this.date),
                        end:
                            this.durationType === 'period'
                                ? new Date(this.end)
                                : new Date(this.date),
                        user_id: Number(this.currentUser.id),
                        active: 'pending',
                        without_pay: this.withoutPay,
                        date:
                            this.durationType === 'day'
                                ? new Date(this.date)
                                : new Date(this.start),
                    },
                    id: this.currentUser.id,
                })

                .then(() => {
                    fireSwal(
                        this.$t('notification.VacationIsCreated'),
                        'success',
                    )
                    this.showWPwarn = false
                    this.requestStatus = false
                    this.date = null
                    this.start = null
                    this.end = null
                    this.withoutPay = false,
                    this.durationType = ''
                    this.duration = ''
                    this.minDate = new Date().toISOString().split('T')[0]
                    this.minDateSecondary = new Date()
                        .toISOString()
                        .split('T')[0]
                    this.maxDate = null
                    this.v$.$reset()
                    this.$emit('closeVacationModal', false)
                    //this.type = this.$t('dashboard.bonusForm.chooseWorker')
                })
                .catch(() => {
                    this.showWPwarn = false
                    this.requestStatus = false
                    // this.defaultState = true
                    // this.date = null
                    // this.start = null
                    // this.end = null
                    // this.durationType = ''
                    // this.duration = ''
                    this.minDate = new Date().toISOString().split('T')[0]
                    this.minDateSecondary = new Date()
                        .toISOString()
                        .split('T')[0]
                    this.maxDate = null
                    this.v$.$reset()
                    fireSwal(
                        this.$t(`notification.VacationIsNotCreated`),
                        'error',
                    )
                })
                .finally(() => {
                    this.v$.reset()
                })
            }
        },
    },
}
</script>

<style lang="scss">
.checkbox-input {
    &-wrapper {
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    &-item {
        margin-right: 8px;
    }

    &-label {
        margin-bottom: 0;
    }
}
.days-count {
    margin-bottom: 10px;
    font-weight: bold;
    &-warn {
        margin-bottom: 10px;
        color: red;
    }
}
</style>
